/**
 * Quick order + reorder for the B2B theme: resolve typed/pasted SKUs via a
 * [data-sku-map] blob, or re-add a past order from a [data-reorder] button.
 */
;(() => {
  const B2B = window.B2B
  const CART_URL = B2B.urls.cart
  const bundleId = B2B.bundleId
  const addItems = B2B.addItems
  const refreshCount = B2B.refreshCount

  // -- Quick order ----------------------------------------------------------

  // Case- and space-insensitive: buyers paste SKUs from spreadsheets that don't
  // preserve case, so both map keys and lookups are normalized through here.
  const skuKey = (sku) =>
    String(sku ?? "")
      .trim()
      .toLowerCase()

  const parseSkuMap = (el) => {
    if (!el) {
      return {}
    }
    try {
      const parsed = JSON.parse(el.textContent)
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        return {}
      }
      const keyed = {}
      for (const [sku, entry] of Object.entries(parsed)) {
        keyed[skuKey(sku)] = entry
      }
      return keyed
    } catch (_e) {
      return {}
    }
  }

  // One page renders inline; the rest are fetched once and merged. Cached per
  // section because the catalogue is stable while the page is open.
  const skuMapCache = new WeakMap()

  const fetchSkuPage = async (scope, page) => {
    const sectionId = scope.dataset.sectionId
    if (!sectionId) {
      return {}
    }
    const url = `${window.location.pathname}?section_id=${encodeURIComponent(
      sectionId
    )}&page=${page}`
    // Never rejects; returns an `ok` flag instead, so the caller can avoid
    // caching a map that silently lacks a page.
    try {
      const res = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "text/html" },
      })
      if (!res.ok) {
        return { map: {}, ok: false }
      }
      const doc = new DOMParser().parseFromString(await res.text(), "text/html")
      return { map: parseSkuMap(doc.querySelector("[data-sku-map]")), ok: true }
    } catch (_e) {
      return { map: {}, ok: false }
    }
  }

  const loadSkuMap = (scope) => {
    const cached = skuMapCache.get(scope)
    if (cached) {
      return cached
    }
    const el = scope.querySelector("[data-sku-map]")
    const first = parseSkuMap(el)
    const pages = Number(el?.dataset.pages) || 1
    const current = Number(el?.dataset.page) || 1

    // Resolved rather than pending when there is nothing to fetch, so the
    // common single-page case adds no await.
    const pending =
      pages <= 1
        ? Promise.resolve(first)
        : Promise.all(
            Array.from({ length: pages }, (_, i) => i + 1)
              .filter((page) => page !== current)
              .map((page) => fetchSkuPage(scope, page))
          ).then((rest) => {
            // A run that lost a page must not become the cached answer; drop it
            // so the next submit fetches again.
            if (rest.some((r) => !r.ok)) {
              skuMapCache.delete(scope)
            }
            // The inline page wins any key collision: it is the one the server
            // rendered for this request.
            return Object.assign({}, ...rest.map((r) => r.map), first)
          })

    skuMapCache.set(scope, pending)
    return pending
  }

  const stockUnitLabel = (count) => `${count} ${count === 1 ? "unit" : "units"}`

  const numOrNull = (value) =>
    value == null || value === "" ? null : Number(value)

  const normalizeSkuEntry = (entry) => {
    if (!entry) {
      return null
    }
    if (typeof entry === "object") {
      return {
        id: entry.id,
        stockLimit: numOrNull(entry.stockLimit),
        stockMax: numOrNull(entry.stockMax),
        available: entry.available !== false,
        min: numOrNull(entry.min),
        max: numOrNull(entry.max),
        increment: numOrNull(entry.increment),
      }
    }
    return {
      id: entry,
      stockLimit: null,
      stockMax: null,
      available: true,
      min: null,
      max: null,
      increment: null,
    }
  }

  // Clamp to the variant's quantity_rule (min/increment/max) so the batch
  // /cart/add can't submit an invalid line. Mirrors b2b-quantity-rules.js.
  const clampToRule = (qty, entry) => {
    if (qty <= 0) {
      return 0
    }
    const min = Number.isFinite(entry.min) && entry.min > 0 ? entry.min : 1
    const step =
      Number.isFinite(entry.increment) && entry.increment > 0
        ? entry.increment
        : 1
    // Two ceilings: the rule's own max and Shopify's sellable stock; the
    // smaller binds, so an accepted number is never refused at add.
    const ruleMax = Number.isFinite(entry.max) ? entry.max : null
    const stockMax = Number.isFinite(entry.stockMax) ? entry.stockMax : null
    let max = ruleMax
    if (stockMax !== null && (max === null || stockMax < max)) {
      max = stockMax
    }

    let value = qty
    if (value < min) {
      value = min
    }
    if (step > 1) {
      const steps = Math.round((value - min) / step)
      value = min + steps * step
    }
    if (max !== null && value > max) {
      const steps = Math.max(0, Math.floor((max - min) / step))
      value = min + steps * step
    }
    return value
  }

  // Lead-time allocation shifts the lead time, never blocks the order; the
  // server recomputes it at ingestion. Mirrors b2b-product.js.
  const leadTimeText = (scope, extended) => {
    const days = extended
      ? scope.dataset.extendedLeadDays || "120"
      : scope.dataset.standardLeadDays || "15"
    return `Up to ${days} days`
  }

  // Stock is per variant, not per row, so rows sharing a SKU are summed first
  // and judged once; a row-by-row check would over-allocate a shared SKU.
  const skuTotals = (scope, map) => {
    const totals = new Map()
    for (const row of scope.querySelectorAll("[data-row]")) {
      const skuInput = row.querySelector("[data-sku]")
      const qtyInput = row.querySelector("[data-qty]")
      const sku = (skuInput && skuInput.value ? skuInput.value : "").trim()
      if (!sku) {
        continue
      }
      const entry = normalizeSkuEntry(map[skuKey(sku)])
      if (!entry?.id) {
        continue
      }
      const qty = qtyInput ? Number(qtyInput.value) || 0 : 0
      totals.set(sku, (totals.get(sku) ?? 0) + clampToRule(qty, entry))
    }
    return totals
  }

  const readRow = (
    row,
    scope,
    map,
    totals,
    channel,
    items,
    missing,
    extended,
    soldOut
  ) => {
    const skuInput = row.querySelector("[data-sku]")
    const qtyInput = row.querySelector("[data-qty]")
    const status = row.querySelector("[data-row-status]")
    const sku = (skuInput && skuInput.value ? skuInput.value : "").trim()
    const qty = qtyInput ? Number(qtyInput.value) || 0 : 0
    if (!sku) {
      if (status) {
        status.textContent = ""
      }
      return
    }
    const entry = normalizeSkuEntry(map[skuKey(sku)])
    if (!entry?.id) {
      missing.push(sku)
      if (status) {
        status.textContent = "✕"
      }
      return
    }
    if (!entry.available || entry.stockLimit === 0) {
      soldOut.push(sku)
      if (status) {
        status.textContent = "Sold out"
      }
      return
    }
    // Snap the typed quantity onto the variant's quantity rule before any
    // stock/line handling so we never post an invalid quantity.
    const ruleQty = clampToRule(qty, entry)
    if (ruleQty !== qty && qtyInput) {
      qtyInput.value = String(ruleQty)
    }
    const skuTotal = totals.get(sku) ?? ruleQty
    const overAllocation =
      entry.stockLimit !== null &&
      Number.isFinite(entry.stockLimit) &&
      skuTotal > entry.stockLimit

    const report = `${sku} (over ${stockUnitLabel(entry.stockLimit)})`
    if (overAllocation && !extended.includes(report)) {
      extended.push(report)
    }

    if (ruleQty > 0) {
      const line = {
        id: entry.id,
        quantity: ruleQty,
        properties: { "Lead time": leadTimeText(scope, overAllocation) },
      }
      if (channel) {
        line.properties._channel = channel
      }
      items.push(line)
      if (status) {
        status.textContent = overAllocation ? "✓ extended" : "✓"
      }
    }
  }

  const addRow = (scope) => {
    const rows = scope.querySelector("[data-rows]")
    const template = rows && rows.querySelector("[data-row]")
    if (!rows || !template) {
      return
    }
    const clone = template.cloneNode(true)
    for (const input of clone.querySelectorAll("input")) {
      if (input.matches("[data-qty]")) {
        input.value = "1"
      } else {
        input.value = ""
      }
    }
    const status = clone.querySelector("[data-row-status]")
    if (status) {
      status.textContent = ""
    }
    rows.appendChild(clone)
  }

  const importCsv = (scope) => {
    const textarea = scope.querySelector("[data-csv]")
    const rows = scope.querySelector("[data-rows]")
    if (!textarea || !rows) {
      return
    }
    const lines = textarea.value.split(/\r?\n/)
    for (const raw of lines) {
      const line = raw.trim()
      if (!line) {
        continue
      }
      const parts = line.split(/[,;\t]/)
      const sku = (parts[0] || "").trim()
      const qty = (parts[1] || "1").trim()
      if (!sku) {
        continue
      }
      addRow(scope)
      const newRow = rows.lastElementChild
      newRow.querySelector("[data-sku]").value = sku
      newRow.querySelector("[data-qty]").value = Number(qty) || 1
    }
    textarea.value = ""
  }

  const fill = (template, value) =>
    (template || "").replace("##", String(value))

  const addAll = async (scope, button) => {
    const status = scope.querySelector("[data-qo-status]")
    const channel = status ? status.dataset.channel : ""
    const id = bundleId()

    // Disabled before the first await: resolving the map can wait on a fetch,
    // and a second click in that window would order everything twice.
    if (button) {
      button.disabled = true
    }
    try {
      const map = await loadSkuMap(scope)
      const items = []
      const missing = []
      const extended = []
      const soldOut = []
      const totals = skuTotals(scope, map)
      for (const row of scope.querySelectorAll("[data-row]")) {
        readRow(
          row,
          scope,
          map,
          totals,
          channel,
          items,
          missing,
          extended,
          soldOut
        )
      }
      for (const item of items) {
        item.properties = { ...(item.properties || {}), _b2b_bundle_id: id }
      }

      if (status) {
        status.className = "quick-order__status"
      }

      if (missing.length > 0 && status) {
        status.textContent = fill(
          status.dataset.notfoundTpl,
          missing.join(", ")
        )
        status.classList.add("quick-order__status--error")
        return
      }
      if (soldOut.length > 0 && status) {
        status.textContent = `These SKUs are not available to order: ${soldOut.join(", ")}.`
        status.classList.add("quick-order__status--error")
        return
      }
      if (items.length === 0) {
        if (status) {
          status.textContent = status.dataset.empty || ""
          status.classList.add("quick-order__status--error")
        }
        return
      }

      try {
        await addItems(items)
        await refreshCount()
        const total = items.reduce((sum, i) => sum + i.quantity, 0)
        if (status) {
          const added = fill(status.dataset.addedTpl, total)
          status.textContent =
            extended.length > 0
              ? `${added} On extended lead time (${leadTimeText(scope, true).toLowerCase()}): ${extended.join(", ")}.`
              : added
          status.classList.add("quick-order__status--ok")
        }
      } catch (err) {
        if (status) {
          status.textContent = err.message
          status.classList.add("quick-order__status--error")
        }
      }
    } finally {
      if (button) {
        button.disabled = false
      }
    }
  }

  document.addEventListener("click", (event) => {
    const t = event.target
    if (!t.closest) {
      return
    }
    const scope = t.closest("[data-quick-order]")
    if (scope) {
      if (t.closest("[data-add-row]")) {
        addRow(scope)
        return
      }
      if (t.closest("[data-csv-import]")) {
        importCsv(scope)
        return
      }
      const addAllBtn = t.closest("[data-add-all]")
      if (addAllBtn) {
        addAll(scope, addAllBtn)
        return
      }
    }

    // -- Reorder (account page) --------------------------------------------
    const reorder = t.closest("[data-reorder]")
    if (reorder) {
      event.preventDefault()
      let items = []
      try {
        items = JSON.parse(reorder.dataset.reorder)
      } catch (_e) {
        items = []
      }
      const channel = reorder.dataset.channel
      const id = bundleId()
      items = items
        .filter((i) => i && i.id)
        .map((i) => ({
          ...i,
          properties: {
            ...(i.properties || {}),
            ...(channel ? { _channel: channel } : {}),
            _b2b_bundle_id: id,
          },
        }))
      if (items.length === 0) {
        return
      }
      reorder.disabled = true
      addItems(items)
        .then(() => {
          window.location.href = CART_URL
        })
        .catch(() => {
          reorder.disabled = false
        })
    }
  })
})()
